/**
 * Detail Book / workstation — Lab `workstation.js` API set.
 *
 * - `getTotalBooks1` — particular books (runners + root NE)
 * - `getBooksForBackend` — market-scoped event group (metadata / fallback)
 * - `getMemberNetExposure` — headline NE for the market
 * - `getBetDetailsForWorkstation` — bet ticker
 *
 * Lab Betfair Book is CompanyMaster-only — agent console does not fetch strips.
 *
 * Demo / no bet URL → fixture book (same as other console tabs).
 */

import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  agentBetFetch,
  betFetch,
  getCallerAgentId,
  hasLiveApi,
  hasLiveBet,
} from '@/api/client'
import { useSession } from '@/api/hooks/shared/useSession'
import { useBookmakerSessionFlag } from '@/api/hooks/shared/useStaffMe'
import { keys } from '@/api/hooks/shared/keys'
import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import type { BookEventGroupBackend } from '@/api/map/books'
import {
  assembleWorkstationBook,
  betfairCompareFromFancy,
  betfairCompareFromMatch,
  emptyBetfairCompareMatch,
  hasDownlineBookPosition,
  isLineSessionMarket,
  marketFromBooksGroups,
  treeNodeFromDownlineBook,
  type BetDetailsForWorkstationRequest,
  type BetDetailsForWorkstationResponse,
  type BetfairBookLineSessionResponse,
  type BetfairBookResponse,
  type BetfairCompareView,
  type GetLadderResponse,
  type InvalidRealTimeBetRequest,
  type InvalidRealTimeBetResponse,
  type MemberNetExposureResponse,
  type RevalidRealTimeBetRequest,
  type RevalidRealTimeBetResponse,
  type TotalBooks1Request,
  type TotalBooks1Response,
} from '@/api/map/workstation'
import { applyLiveRatesToBook, fancyRatesFromBets, mergeLiveRatePayload } from '@/api/map/rates'
import { listChildren } from '@/api/hooks/downline/useChildren'
import { ROLE_LABEL, type Account, type Role } from '@/data/types'
import type { BookType } from '@/features/net-exposure/fixture'
import {
  defaultBetDateRange,
  getWorkstationBook,
  type WorkstationBook,
  type WorkstationMarketType,
  type WorkstationRunner,
  type WorkstationTreeNode,
} from '@/features/net-exposure/workstation-fixture'
import { BOOKS_POLL_MS } from './useBooks'
import { useMarketRatesStream } from './useMarketRatesStream'
import {
  buildFancyPtLadderBody,
  fancyPtLadderRoute,
  fancyPtLadderUsesTotal,
} from './fancyPtLadder'

/** Lab line-session BF book polls every 5s. */
export const BETFAIR_BOOK_POLL_MS = 5_000

/** Lab bet ticker polls every 3s (workstation.js ~2–5s). */
export const BET_TICKER_POLL_MS = 3_000

export type WorkstationQuery = {
  marketId: string
  selectedType: BookType
  /** Search-param fallbacks. */
  eventName?: string
  marketName?: string
  type?: WorkstationMarketType
  /** Net Exposure sport (Horse Racing / Greyhound Racing). */
  eventType?: string
  /** Bet ticker filters (Lab getBetList). */
  page?: number
  maxRow?: number
  fromDate?: string
  toDate?: string
  userName?: string | string[]
  currencyType?: string
}

export type WorkstationBetFilters = Pick<
  WorkstationQuery,
  'page' | 'maxRow' | 'fromDate' | 'toDate' | 'userName' | 'currencyType'
>

function labLevelLabel(role: Role | undefined): string {
  if (!role) return 'Master'
  return ROLE_LABEL[role]
}

function liveReady(user: string | undefined | null): user is string {
  return Boolean(hasLiveApi() && hasLiveBet() && user)
}

async function loadTotalBooks1(
  query: WorkstationQuery,
  user: string,
  role: Role | undefined,
  username: string | undefined,
): Promise<TotalBooks1Response> {
  const body: TotalBooks1Request = {
    user,
    marketId: query.marketId,
    loggedInMember: user,
    username: username || undefined,
    accessLevel: labLevelLabel(role),
    selectedType: query.selectedType,
    page: 1,
  }
  return betFetch<TotalBooks1Response>('/v1/book/getTotalBooks1', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

async function loadBooksForMarket(
  query: WorkstationQuery,
  user: string,
  role: Role | undefined,
  bookmakerSessionFlag: BookmakerSessionFlag,
): Promise<BookEventGroupBackend[]> {
  return betFetch<BookEventGroupBackend[]>('/v1/book/getBooksForBackend', {
    method: 'POST',
    body: JSON.stringify({
      selectedType: query.selectedType,
      filter: {
        user,
        level: labLevelLabel(role),
        marketId: query.marketId,
        eventname: 'All',
        bookmakerSessionFlag,
      },
    }),
  })
}

async function loadMemberNetExposure(
  marketId: string,
  user: string,
): Promise<MemberNetExposureResponse> {
  return betFetch<MemberNetExposureResponse>('/v1/book/getMemberNetExposure', {
    method: 'POST',
    body: JSON.stringify({ _id: user, marketId }),
  })
}

async function loadBetDetails(
  query: WorkstationQuery,
  user: string,
  role: Role | undefined,
): Promise<BetDetailsForWorkstationResponse> {
  const defaults = defaultBetDateRange()
  const body: BetDetailsForWorkstationRequest = {
    user,
    marketId: query.marketId,
    accessLevel: labLevelLabel(role),
    page: query.page ?? 1,
    maxRow: query.maxRow ?? 100,
    fromDate: query.fromDate || defaults.fromDate,
    toDate: query.toDate || defaults.toDate,
    currencyType: query.currencyType || 'All',
  }
  if (query.userName != null && query.userName !== '') body.userName = query.userName
  return betFetch<BetDetailsForWorkstationResponse>('/v1/bets/getBetDetailsForWorkstation', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

async function loadBetfairBook(
  marketId: string,
  lineSession: boolean,
): Promise<BetfairBookResponse | BetfairBookLineSessionResponse> {
  const path = lineSession
    ? '/v1/bet/getBetfairBookLineSession'
    : '/v1/bet/getBetfairBook'
  return betFetch(path, {
    method: 'POST',
    body: JSON.stringify({ marketId }),
  })
}

type DownlineBookInput = {
  parentUserId: string
  marketId: string
  selectedType: BookType
  runners: WorkstationRunner[]
  marketType: WorkstationMarketType
}

/** One direct child → getTotalBooks1 (Lab getBookForworkStation per kid). */
async function loadOneDirectChildBook(
  child: Account,
  input: DownlineBookInput,
): Promise<WorkstationTreeNode | null> {
  const res = await betFetch<TotalBooks1Response>('/v1/book/getTotalBooks1', {
    method: 'POST',
    body: JSON.stringify({
      user: child.id,
      marketId: input.marketId,
      loggedInMember: child.id,
      username: child.username || undefined,
      accessLevel: ROLE_LABEL[child.role],
      selectedType: input.selectedType,
      page: 1,
    } satisfies TotalBooks1Request),
  })
  const row = res.book?.[0]
  if (!hasDownlineBookPosition(row)) return null
  return treeNodeFromDownlineBook(row!, input.runners, input.marketType, {
    username: child.username || row!.username,
    level: child.role === 'player' ? 'player' : 'agent',
    accessLevel: ROLE_LABEL[child.role],
  })
}

/**
 * Lab `showDownLine` → `getParticularBooks` without accessLevel
 * (`getImmediateChildren` + `getBookForDownline`).
 *
 * No downline batch on Agent-API bet-service yet — fan-out getTotalBooks1 across
 * **direct children only** (not full subtree).
 */
export async function loadDownlineParticularBooks(
  input: DownlineBookInput,
): Promise<WorkstationTreeNode[]> {
  const kids = await listChildren(input.parentUserId)
  // ponytail: ceiling = one request per direct child until getBookForDownline batch ships.
  const pending: Promise<WorkstationTreeNode | null>[] = []
  for (const child of kids) {
    pending.push(loadOneDirectChildBook(child, input))
  }
  const nodes = await Promise.all(pending)
  return nodes
    .filter((n): n is WorkstationTreeNode => Boolean(n))
    .sort((a, b) => a.username.localeCompare(b.username))
}

async function invalidWorkstationBets(
  body: InvalidRealTimeBetRequest,
): Promise<InvalidRealTimeBetResponse> {
  return agentBetFetch<InvalidRealTimeBetResponse>('/v1/bet/invalidRealTimeBet', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

async function revalidateWorkstationBet(
  body: RevalidRealTimeBetRequest,
): Promise<RevalidRealTimeBetResponse> {
  return agentBetFetch<RevalidRealTimeBetResponse>('/v1/bet/revalidRealTimeBet', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

function useLiveIdentity() {
  const { data: session } = useSession()
  const user = getCallerAgentId() ?? session?.account.id
  const role = session?.account.role
  const username = session?.account.username
  const live = liveReady(user)
  return { session, user, role, username, live }
}

/** Lab `getParticularBooks` → `POST /v1/book/getTotalBooks1`. */
export function useTotalBooks1(query: WorkstationQuery) {
  const { session, user, role, username, live } = useLiveIdentity()
  return useQuery({
    queryKey: keys.workstationTotalBooks({
      marketId: query.marketId,
      selectedType: query.selectedType,
      user,
      level: labLevelLabel(role),
    }),
    queryFn: () => loadTotalBooks1(query, user!, role, username),
    enabled: Boolean(session && live && query.marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Lab family-icon expand — downline books for one parent on this market.
 * Polls while open (Lab `timeoutChildren` refresh on book change).
 */
export function useDownlineParticularBooks(input: {
  parentUserId: string
  marketId: string
  selectedType: BookType
  runners: WorkstationRunner[]
  marketType: WorkstationMarketType
  enabled: boolean
}) {
  return useQuery({
    queryKey: keys.workstationDownlineBooks({
      parentUserId: input.parentUserId,
      marketId: input.marketId,
      selectedType: input.selectedType,
    }),
    queryFn: () =>
      loadDownlineParticularBooks({
        parentUserId: input.parentUserId,
        marketId: input.marketId,
        selectedType: input.selectedType,
        runners: input.runners,
        marketType: input.marketType,
      }),
    enabled: Boolean(input.enabled && input.parentUserId && input.marketId),
    refetchInterval: input.enabled ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab PT ladder poll while open (~1.5s). */
export const PT_LADDER_POLL_MS = 1_500

/**
 * Lab `showRunners` → `Book/getLadder` or `getLadderTotal`.
 * Total Book + non-player uses getLadderTotal (player lt/gt flipped).
 */
export function useFancyPtLadder(input: {
  marketId: string
  userId: string
  accessLevel?: string
  selectedType: BookType
  nodeLevel: 'agent' | 'player'
  enabled: boolean
  /** Betfair line session → ladder body type betfair-line-sessions. */
  lineSession?: boolean
}) {
  const totalBook = fancyPtLadderUsesTotal(input.selectedType, input.nodeLevel)
  const lineSession = Boolean(input.lineSession)
  return useQuery({
    queryKey: [
      'agent',
      'workstation-pt-ladder',
      {
        marketId: input.marketId,
        userId: input.userId,
        accessLevel: input.accessLevel,
        selectedType: input.selectedType,
        totalBook,
        lineSession,
      },
    ],
    queryFn: async () => {
      const body = buildFancyPtLadderBody({
        marketId: input.marketId,
        userId: input.userId,
        accessLevel: input.accessLevel,
        selectedType: input.selectedType,
        nodeLevel: input.nodeLevel,
        agentFallbackLevel: 'Master',
        lineSession,
      })
      const route = fancyPtLadderRoute(input.selectedType, input.nodeLevel)
      return betFetch<GetLadderResponse>(`/v1/book/${route}`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    enabled: Boolean(input.enabled && input.marketId && input.userId),
    refetchInterval: input.enabled ? PT_LADDER_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab workstation `getBook` with marketId → `POST /v1/book/getBooksForBackend`. */
export function useWorkstationBooks(query: WorkstationQuery) {
  const { session, user, role, live } = useLiveIdentity()
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  return useQuery({
    queryKey: keys.workstationBooks({
      marketId: query.marketId,
      selectedType: query.selectedType,
      user,
      level: labLevelLabel(role),
      bookmakerSessionFlag,
    }),
    queryFn: () => loadBooksForMarket(query, user!, role, bookmakerSessionFlag),
    enabled: Boolean(session && live && query.marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab `getMarketNetExposure` → `POST /v1/book/getMemberNetExposure`. */
export function useMemberNetExposure(marketId: string) {
  const { session, user, live } = useLiveIdentity()
  return useQuery({
    queryKey: keys.workstationMemberNe({ marketId, user }),
    queryFn: () => loadMemberNetExposure(marketId, user!),
    enabled: Boolean(session && live && marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab `getBetList` → `POST /v1/bets/getBetDetailsForWorkstation`. */
export function useBetDetailsForWorkstation(query: WorkstationQuery) {
  const { session, user, role, live } = useLiveIdentity()
  return useQuery({
    queryKey: keys.workstationBets({
      marketId: query.marketId,
      user,
      level: labLevelLabel(role),
      page: query.page ?? 1,
      maxRow: query.maxRow ?? 100,
      fromDate: query.fromDate,
      toDate: query.toDate,
      userName: query.userName,
      currencyType: query.currencyType,
    }),
    queryFn: () => loadBetDetails(query, user!, role),
    enabled: Boolean(session && live && query.marketId),
    refetchInterval: live ? BET_TICKER_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Lab `getBetfairBook` — CompanyMaster only in Lab workstation.js.
 * Agent console never enables this query.
 */
export function useBetfairBook(input: {
  marketId: string
  /** Prefer Lab raw type (`lineLadder`); UI maps line → fancy layout. */
  marketType?: WorkstationMarketType | string
  marketName?: string
  lineLadder?: boolean
}) {
  const lineSession = isLineSessionMarket({
    marketType: input.marketType,
    marketId: input.marketId,
    marketName: input.marketName,
    lineLadder: input.lineLadder,
  })
  return useQuery({
    queryKey: keys.workstationBetfairBook({ marketId: input.marketId, lineSession }),
    queryFn: () => loadBetfairBook(input.marketId, lineSession),
    enabled: false,
    retry: false,
  })
}

export function useInvalidateWorkstationBets() {
  const client = useQueryClient()
  const { username } = useLiveIdentity()

  return useMutation({
    mutationFn: (input: { betIds: string[]; reason: string }) =>
      invalidWorkstationBets({
        _id: input.betIds,
        reason: input.reason,
        invalidBy: username || undefined,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['workstation-total-books'] })
      client.invalidateQueries({ queryKey: ['workstation-books'] })
      client.invalidateQueries({ queryKey: ['workstation-member-ne'] })
      client.invalidateQueries({ queryKey: ['workstation-bets'] })
      client.invalidateQueries({ queryKey: ['workstation-betfair-book'] })
      client.invalidateQueries({ queryKey: ['workstation-downline-books'] })
    },
  })
}

export function useRevalidateWorkstationBet() {
  const client = useQueryClient()
  const { username } = useLiveIdentity()

  return useMutation({
    mutationFn: (input: { betId: string }) =>
      revalidateWorkstationBet({
        _id: input.betId,
        revalidBy: username || undefined,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['workstation-total-books'] })
      client.invalidateQueries({ queryKey: ['workstation-books'] })
      client.invalidateQueries({ queryKey: ['workstation-member-ne'] })
      client.invalidateQueries({ queryKey: ['workstation-bets'] })
      client.invalidateQueries({ queryKey: ['workstation-betfair-book'] })
      client.invalidateQueries({ queryKey: ['workstation-downline-books'] })
    },
  })
}

/**
 * Compose Lab workstation payloads into one `WorkstationBook`.
 * Demo / offline → fixture. Live with empty APIs → null book (empty UI).
 */
export function useWorkstationBook(query: WorkstationQuery) {
  const { live, username, role } = useLiveIdentity()
  const totalQ = useTotalBooks1(query)
  const booksQ = useWorkstationBooks(query)
  const neQ = useMemberNetExposure(query.marketId)
  const betsQ = useBetDetailsForWorkstation(query)
  const ratesQ = useMarketRatesStream(live ? query.marketId : undefined)

  const fixture = useMemo(
    () => (query.marketId ? getWorkstationBook(query.marketId) : undefined),
    [query.marketId],
  )

  const book = useMemo((): WorkstationBook | null | undefined => {
    if (!live) return fixture ?? null
    const booksMarket = marketFromBooksGroups(booksQ.data, query.marketId)
    const assembled = assembleWorkstationBook({
      marketId: query.marketId,
      bookType: query.selectedType,
      total: totalQ.data,
      booksMarket,
      bets: betsQ.data,
      memberNe: neQ.data?.netExposure,
      fallback: {
        eventName: query.eventName || fixture?.eventName,
        marketName: query.marketName || fixture?.marketName,
        type: query.type || fixture?.type,
        eventType: query.eventType || fixture?.eventType,
        username,
        accessLevel: labLevelLabel(role),
      },
    })
    if (!assembled) return assembled
    // Catalog HTTP seed (getTotalBooks1.liveRates) = Frontend Player-Markets
    // first paint. SSE overlays on top; fancy can still seed from open bets.
    let ratesPayload = ratesQ.payload
    const catalogRates = totalQ.data?.liveRates ?? null
    if (catalogRates) {
      ratesPayload = ratesPayload
        ? mergeLiveRatePayload(catalogRates, ratesPayload)
        : catalogRates
    }
    // Custom fancy only — BF Line Session uses live EX ladder (not bet Yes/No seed).
    if (assembled.type === 'fancy' && !assembled.lineLadder) {
      const fromBets = fancyRatesFromBets(assembled.bets)
      if (fromBets) {
        ratesPayload = ratesPayload
          ? mergeLiveRatePayload(fromBets, ratesPayload)
          : fromBets
      }
    }
    if (!ratesPayload) return assembled
    return applyLiveRatesToBook(assembled, ratesPayload)
  }, [
    live,
    fixture,
    query.marketId,
    query.selectedType,
    query.eventName,
    query.marketName,
    query.type,
    query.eventType,
    totalQ.data,
    booksQ.data,
    betsQ.data,
    neQ.data?.netExposure,
    username,
    role,
    ratesQ.payload,
  ])

  const marketType = book?.type ?? query.type
  const marketName = book?.marketName || query.marketName
  const booksMarket = marketFromBooksGroups(booksQ.data, query.marketId)
  const labBookType = totalQ.data?.type
  const lineLadderFlag = Boolean(
    book?.lineLadder ||
      totalQ.data?.book?.[0]?.lineLadder ||
      booksMarket?.lineLadder ||
      labBookType === 'lineLadder',
  )
  const lineSession = isLineSessionMarket({
    marketType: labBookType ?? marketType,
    marketId: query.marketId,
    marketName,
    lineLadder: lineLadderFlag,
  })
  const betfairQ = useBetfairBook({
    marketId: query.marketId,
    marketType: labBookType ?? marketType,
    marketName,
    lineLadder: lineLadderFlag,
  })

  const betfairCompare = useMemo((): BetfairCompareView | null => {
    if (!live) return null
    const runners = book?.runners ?? []
    if (!betfairQ.data) {
      return !lineSession && runners.length > 0
        ? emptyBetfairCompareMatch(runners)
        : null
    }
    const data = betfairQ.data
    if (lineSession) {
      const line = data as BetfairBookLineSessionResponse
      return betfairCompareFromFancy(
        line.bookFromLocal,
        line.bookFromBetfair,
        marketName || query.marketId,
      )
    }
    const match = data as BetfairBookResponse
    return (
      betfairCompareFromMatch(match.bookFromLocal, match.bookFromBetfair, runners) ??
      (runners.length > 0 ? emptyBetfairCompareMatch(runners) : null)
    )
  }, [
    live,
    betfairQ.data,
    lineSession,
    marketName,
    book?.runners,
    query.marketId,
  ])

  const isLoading =
    live &&
    !totalQ.data &&
    !booksQ.data &&
    (totalQ.isLoading || booksQ.isLoading)

  const isError = live && totalQ.isError && booksQ.isError
  const error = totalQ.error ?? booksQ.error ?? betsQ.error ?? neQ.error
  const isFetching =
    totalQ.isFetching ||
    booksQ.isFetching ||
    betsQ.isFetching ||
    neQ.isFetching ||
    betfairQ.isFetching

  return {
    book: book === undefined ? null : book,
    betfairCompare,
    memberNetExposure: live ? (neQ.data?.netExposure ?? null) : null,
    betCount: live ? (betsQ.data?.count ?? book?.bets.length ?? 0) : (book?.bets.length ?? 0),
    isLoading,
    isError,
    error,
    isFetching,
    live,
    refetch: () => {
      void totalQ.refetch()
      void booksQ.refetch()
      void neQ.refetch()
      void betsQ.refetch()
      void betfairQ.refetch()
    },
    ratesStatus: ratesQ.status,
  }
}
